import React, { useRef, useState, useEffect } from "react";
import galleryPhoto01 from "../../assets/Koala/01.jpg"
import galleryPhoto02 from "../../assets/Koala/02.jpg"
import galleryPhoto03 from "../../assets/Koala/03.jpg"
import galleryPhoto04 from "../../assets/Koala/04.jpg"
import galleryPhoto05 from "../../assets/Koala/05.jpeg"
import galleryPhoto07 from "../../assets/Koala/07.jpg"
import galleryPhoto08 from "../../assets/Koala/08.jpg"
import galleryPhoto09 from "../../assets/Koala/09.png"
import galleryPhoto10 from "../../assets/Koala/10.jpg"

// List of threats with corresponding images
const speciesList = [
  { id: 1, title: "Drought", image: galleryPhoto01 },
  { id: 2, title: "Bush Fire", image: galleryPhoto07 },
  { id: 3, title: "Flood", image: galleryPhoto04 },
  { id: 4, title: "Over Logging", image: galleryPhoto09 },
  { id: 5, title: "Habitat Loss", image: galleryPhoto10 },
  { id: 6, title: "Be Attacked", image: galleryPhoto02 },
  { id: 7, title: "Accident", image: galleryPhoto08 },
  { id: 8, title: "Environmental Stress", image: galleryPhoto03 },
  { id: 9, title: "Human Disturbance", image: galleryPhoto05 },
];

export default function HomePageGallery() {
  const scrollRef = useRef<HTMLDivElement>(null); // Reference to the scroll container
  const [activeIndex, setActiveIndex] = useState(0); // Current active dot index
  const [dotsCount, setDotsCount] = useState(0);     // Total number of dots
  const dotsCountRef = useRef(0);
  const cardWidthWithSpacing = 336;

  useEffect(() => {
    dotsCountRef.current = dotsCount;
  }, [dotsCount]);

  // Recalculate how many dots are needed when resizing the window
  useEffect(() => {
    const updateDotsCount = () => {
      if (scrollRef.current) {
        const containerWidth = scrollRef.current.offsetWidth;
        const visibleCards = Math.floor(containerWidth / cardWidthWithSpacing);
        const dotCount = speciesList.length - visibleCards + 1;
        setDotsCount(dotCount);
      }
    };

    updateDotsCount();
    window.addEventListener("resize", updateDotsCount);
    return () => window.removeEventListener("resize", updateDotsCount);
  }, []);

  // Scroll carousel
  const scrollCarousel = (targetIndex: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: targetIndex * cardWidthWithSpacing,
        behavior: "smooth",
      });
    }
  };

  // Update active dot index when user scrolls
  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const offsetScroll = scrollLeft;
      const index = Math.floor((offsetScroll + cardWidthWithSpacing / 2) / cardWidthWithSpacing);
      const currentIndex = Math.min(Math.max(index, 0), dotsCountRef.current - 1);
      setActiveIndex(currentIndex);
    }
  };

  // Add scroll listener
  useEffect(() => {
    const node = scrollRef.current;
    if (node) {
      node.addEventListener("scroll", handleScroll);
      return () => node.removeEventListener("scroll", handleScroll);
    }
  }, []);

  return (
    <section className="custom-section">
      <div className="max-w-6xl mx-auto py-10">
        <h2 className="text-5xl font-bold mb-4">Threats to Koalas</h2>

        <div className="relative w-11/12 md:w-full m-auto">
          {/* Horizontal scrollable carousel */}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory space-x-4 pb-4 scrollbar-hide"
          >
            {speciesList.map((species) => (
              <div
                key={species.id}
                className="snap-center min-w-[320px] flex-shrink-0 rounded-xl overflow-hidden shadow-md relative"
              >
                <div className="aspect-[4/3] w-[320px]">
                  <img
                    src={species.image}
                    alt={species.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Title overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-2">
                  <p className="text-white text-lg font-semibold">
                    {species.title}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Scroll buttons */}
          {activeIndex > 0 &&
            <button
              onClick={() => scrollCarousel(activeIndex - 1)}
              className="absolute left-[-30px] top-1/2 -translate-y-[100%] z-10 bg-white rounded-full w-10 h-10 shadow-md flex items-center justify-center"
            >
              {/* Left arrow icon */}
              <svg width="12" height="24" viewBox="0 0 18 32" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M16.1198 0C16.8392 0.000205755 17.4877 0.433626 17.763 1.09829C18.0383 1.7629 17.8861 2.52802 17.3775 3.03667L4.40333 16.0109L17.3775 28.9851C18.0517 29.6832 18.0613 30.7927 17.3751 31.4789C16.6889 32.1652 15.5793 32.1748 14.8813 31.5006L0.649366 17.2687C-0.0451493 16.574 -0.0451493 15.4479 0.649366 14.7532L14.8813 0.521216C15.2149 0.187576 15.6673 1.52671e-07 16.1391 0Z" fill="#B6B9BB" />
              </svg>
            </button>
          }

          {((activeIndex+1) < dotsCount) &&
            <button
              onClick={() => scrollCarousel(activeIndex + 1)}
              className="absolute right-[-30px] top-1/2 -translate-y-[100%] z-10 bg-white rounded-full w-10 h-10 shadow-md flex items-center justify-center"
            >
              {/* Right arrow icon */}
              <svg width="12" height="24" viewBox="0 0 18 32" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M1.88023 32C1.16081 31.9998 0.512299 31.5664 0.237011 30.9017C-0.0382774 30.2371 0.113851 29.472 0.622479 28.9633L13.5967 15.9891L0.622479 3.01488C-0.0517093 2.31684 -0.0420675 1.20727 0.64415 0.521055C1.33037 -0.165162 2.43994 -0.174804 3.13797 0.499384L17.3699 14.7313C18.0644 15.426 18.0644 16.5521 17.3699 17.2468L3.13797 31.4788C2.80443 31.8124 2.35201 31.9999 1.88023 32Z" fill="#B6B9BB" />
              </svg>
            </button>
          }


          {/* Dots indicator */}
          <div className="mt-2 flex justify-center">
            <ul className="flex justify-center mt-4">
              {Array.from({ length: dotsCount }).map((_, i) => (
                <li key={i}>
                  <button
                    onClick={() => scrollCarousel(i)}
                    className={`w-3 h-3 mx-1 rounded-full transition-all ${i === activeIndex ? "bg-primary" : "bg-secondary"
                      }`}
                  ></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}